import React, { useMemo } from 'react';
import { ShoppingCart, IndianRupee, Package, AlertTriangle, BarChart3, TrendingUp } from 'lucide-react';

// Components
import { StatCard } from './CommonComponents';

// Helpers
import { formatCurrency, formatDate } from '../Helpers';


const Dashboard = ({ user, inventory, sales, shopName = 'StoreFlow' }) => {
    const stats = useMemo(() => {
        const totalItems = inventory.length;
        const totalStock = inventory.reduce((sum, item) => sum + item.quantity, 0);
        const inventoryValue = inventory.reduce((sum, item) => sum + (item.purchasePrice * item.quantity), 0);
        const totalRevenue = sales.reduce((sum, sale) => sum + sale.totalAmount, 0);
        const lowStockItems = inventory.filter(item => item.quantity <= 10).length;

        return { totalItems, totalStock, inventoryValue, totalRevenue, lowStockItems };
    }, [inventory, sales]);

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 sm:p-8 text-white">
                <div className="flex items-center space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-3 sm:p-4">
                        <BarChart3 className="h-8 w-8 sm:h-12 sm:w-12" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold">{shopName} - Dashboard</h1>
                        <p className="text-blue-100 mt-1 text-sm sm:text-base">Welcome back, {user.email}</p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <StatCard title="Total Revenue" value={formatCurrency(stats.totalRevenue)} icon={IndianRupee} color="green" />
                <StatCard title="Inventory Value" value={formatCurrency(stats.inventoryValue)} icon={Package} color="blue" />
                <StatCard title="Total Stock Units" value={stats.totalStock.toLocaleString('en-IN')} icon={ShoppingCart} color="purple" />
                <StatCard title="Low Stock Items" value={stats.lowStockItems} icon={AlertTriangle} color="red" />
            </div>

            {/* Recent Sales */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                        Recent Sales
                    </h2>
                </div>
                <div className="p-6">
                    {sales.length === 0 ? (
                        <div className="text-center py-8">
                            <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No sales recorded yet</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table View */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full text-left min-w-[500px]">
                                    <thead>
                                        <tr className="bg-gray-50 border-b">
                                            <th className="p-2 sm:p-3 font-medium text-gray-600 text-sm">Date</th>
                                            <th className="p-2 sm:p-3 font-medium text-gray-600 text-sm">Items</th>
                                            <th className="p-2 sm:p-3 font-medium text-gray-600 text-right text-sm">Total Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sales.slice(0, 5).map(sale => (
                                            <tr key={sale.id} className="border-b hover:bg-gray-50 transition-colors">
                                                <td className="p-2 sm:p-3 text-gray-700 text-sm">{formatDate(sale.saleDate)}</td>
                                                <td className="p-2 sm:p-3 text-gray-700 text-sm">
                                                    <div className="truncate max-w-[150px] sm:max-w-none">
                                                        {sale.items.map(i => i.itemName).join(', ')}
                                                    </div>
                                                </td>
                                                <td className="p-2 sm:p-3 text-gray-800 font-semibold text-right text-sm">{formatCurrency(sale.totalAmount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="sm:hidden space-y-3">
                                {sales.slice(0, 5).map(sale => (
                                    <div key={sale.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                <ShoppingCart className="h-4 w-4 text-blue-600" />
                                                <p className="font-medium text-gray-900 text-sm">{formatDate(sale.saleDate)}</p>
                                            </div>
                                            <div className="bg-green-50 rounded-lg px-2 py-1">
                                                <p className="text-sm font-bold text-green-600">{formatCurrency(sale.totalAmount)}</p>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-600 truncate">
                                            {sale.items.map(i => i.itemName).join(', ')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;